import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { SearchContextType, Tool } from '../types';
import { tools } from '../data/tools';

const SearchContext = createContext<SearchContextType | undefined>(undefined);

interface SearchProviderProps {
  children: ReactNode;
}

export const SearchProvider = ({ children }: SearchProviderProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredTools, setFilteredTools] = useState<Tool[]>(tools);
  const [recentTools, setRecentTools] = useState<Tool[]>([]);

  useEffect(() => {
    const savedRecent = localStorage.getItem('recentTools');
    if (savedRecent) {
      try {
        const parsed = JSON.parse(savedRecent) as string[];
        const recentToolsData = parsed
          .map(id => tools.find(tool => tool.id === id))
          .filter((tool): tool is Tool => tool !== undefined)
          .slice(0, 3);
        setRecentTools(recentToolsData);
      } catch (error) {
        console.error('Error parsing recent tools:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredTools(tools);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = tools.filter(tool => 
      tool.name.toLowerCase().includes(query) ||
      tool.description.toLowerCase().includes(query) ||
      tool.category.toLowerCase().includes(query) ||
      tool.tags.some(tag => tag.toLowerCase().includes(query))
    );

    setFilteredTools(filtered);
  }, [searchQuery]);

  const addToRecent = useCallback((tool: Tool) => {
    setRecentTools(prev => {
      const filtered = prev.filter(t => t.id !== tool.id);
      const newRecent = [tool, ...filtered].slice(0, 3);
      localStorage.setItem('recentTools', JSON.stringify(newRecent));
      return newRecent;
    });
  }, []);

  const clearRecentTools = () => {
    setRecentTools([]);
    try {
      localStorage.removeItem('recentTools');
    } catch (error) {
      console.error('Error clearing recent tools:', error);
    }
  };

  const value: SearchContextType = {
    searchQuery,
    setSearchQuery,
    filteredTools,
    recentTools,
    addToRecent,
    clearRecentTools,
  };

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = (): SearchContextType => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};